import { createContext, useState, useEffect, ReactNode, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { api } from '@/services/api'
import type { UserPublic } from '@/services/types'

interface AuthContextType {
  user: UserPublic | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserPublic | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getCurrentUser()
      setUser(userData)
      Sentry.setUser({ id: userData.id, email: userData.email })
    } catch {
      setUser(null)
      Sentry.setUser(null)
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      refreshUser().finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [refreshUser])

  const login = async (email: string, password: string) => {
    const response = await api.login(email, password)
    setUser(response.user)
    Sentry.setUser({ id: response.user.id, email: response.user.email })
  }

  const register = async (email: string, password: string) => {
    // Register doesn't automatically log in per the backend spec
    await api.register(email, password)
    // So we need to login after registration
    const loginResponse = await api.login(email, password)
    setUser(loginResponse.user)
    Sentry.setUser({ id: loginResponse.user.id, email: loginResponse.user.email })
  }

  const logout = async () => {
    try {
      await api.logout()
    } finally {
      setUser(null)
      Sentry.setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
